<template>
  <Field
    as="div"
    :name="name || ''"
    :rules="rules"
    v-slot="{ field, errorMessage }"
    :model-value="currentValue"
  >
    <div class="relative w-full flex items-center gap-2">
      <input
        type="checkbox"
        v-bind="field"
        :id="id"
        :name="name"
        :required="required"
        :disabled="disabled"
        :readonly="readonly"
        :value="currentValue"
        ref="inputRef"
        class="absolute opacity-0 w-px h-px focus:outline-none"
        @input="handleInput"
        @change="handleInput"
        @focus="isFocused = true"
        @blur="isFocused = false"
      />

      <label :for="id" class="flex items-center cursor-pointer select-none">
        <span
          :class="[
            'w-6 h-6 shrink-0 flex items-center justify-center bg-surface-light border rounded-sm transition-colors',
            isFocused ? 'ring-1 ring-input-border' : '',
            currentValue ? 'border-primary' : ' border-input-border',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
          ]"
        >
          <CheckmarkIcon
            v-if="currentValue"
            class="block w-3 h-3 fill-primary transition-colors"
          />
        </span>
        <span :class="['ml-4', errorMessage ? 'text-error' : 'text-text']">
          <slot><span v-html="label" /></slot>
        </span>
      </label>
    </div>
    <div v-if="errorMessage" class="text-sm block pt-2 text-error">
      {{ errorMessage }}
    </div>
  </Field>
</template>

<script setup lang="ts">
  import { Field } from 'vee-validate';
  import { computed,ref } from 'vue';

  import CheckmarkIcon from '@/components/icons/CheckmarkIcon.vue';

  import { type InputProps } from './InputMixin';

  interface Props extends InputProps<boolean> {
    label?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
  });

  const internalValue = ref(false);
  const isFocused = ref(false);

  const currentValue = computed(() => {
    return props.modelValue != undefined
      ? props.modelValue
      : internalValue.value;
  });

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
  }>();

  const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    emit('update:modelValue', target.checked);
  };
</script>
